// =============================================================
// 🧩 ModalEditEquipe.tsx — Platinum PRO 2025 (Backend Aligned)
// =============================================================

import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { api } from "@/lib/axios"; // ✅ aligné avec tes autres pages
import { Agent, Equipe } from "@/types/planificateur";
import { useAuth } from "@/context/AuthContext";

interface Props {
  equipe: Equipe;
  onClose: () => void;
  onSuccess: () => void;
}

type AgentExtended = Agent & {
  fonction?: string | null;
  actif?: boolean;
};
 
const ModalEditEquipe: React.FC<Props> = ({
  equipe,
  onClose,
  onSuccess,
}) => {
  const [agents, setAgents] = useState<AgentExtended[]>([]);
  const [selected, setSelected] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState<"all" | "chef" | "chauffeur" | "agent">("all");

// =============================================================
// 🚀 Chargement des AGENTS RH — ModalEditEquipe (CORRIGÉ & STABLE)
// =============================================================
const { accessToken, isLoading } = useAuth();

useEffect(() => {

  if (isLoading) return;

  const currentToken = accessToken || localStorage.getItem("accessToken");

  if (!currentToken) {
    toast.error("Session expirée.");
    return;
  }

  let mounted = true;

  const loadAgents = async () => {
    setLoading(true);

    try {

      const res = await api.get("/agents", {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });

      const rawData = Array.isArray(res.data)
        ? res.data
        : res.data?.data ?? [];

      const agentsData = rawData.map((a: any) => ({
        ...a,
        actif: a.actif ?? true,
      }));

      if (mounted) {

        setAgents(agentsData);

        if (equipe?.agents) {
          setSelected(equipe.agents.map((a) => a.id));
        }

      }

    } catch (err) {

      console.error("Erreur agents :", err);

      if (mounted) setAgents([]);

    } finally {

      if (mounted) setLoading(false);

    }
  };

  loadAgents();

  return () => {
    mounted = false;
  };

}, [accessToken, isLoading, equipe]);

// Debug : pour voir si les données arrivent enfin
useEffect(() => {
  if (agents.length > 0) {
    console.log("✅ Agents chargés avec succès :", agents);
  }
}, [agents]);

useEffect(() => {
  console.log("Agents chargés :", agents);
}, [agents]);

  // =============================================================
  // ⚙️ Toggle agent
  // =============================================================
const toggleAgent = (id: number) => {
  setSelected(prev => {
    const set = new Set(prev);
    set.has(id) ? set.delete(id) : set.add(id);
    return Array.from(set);
  });
};

// =============================================================
// 🧠 TRI + FILTRAGE INTELLIGENT
// =============================================================
const sortedAgents = [...agents].sort((a, b) => {
  const getPriority = (agent: AgentExtended): number => {
    const f = agent.fonction?.toLowerCase();

    if (f === "chef") return 1;
    if (f === "chauffeur") return 2;
    return 3;
  };

  return getPriority(a) - getPriority(b);
});

const filteredAgents = sortedAgents.filter((agent) => {
  if (filter === "all") return true;
  if (filter === "chef")
    return agent.fonction?.toLowerCase() === "chef";
  if (filter === "chauffeur")
    return agent.fonction?.toLowerCase() === "chauffeur";
  if (filter === "agent")
    return !agent.fonction ||
      !["chef", "chauffeur"].includes(
        agent.fonction.toLowerCase()
      );
  return true;
});

  // =============================================================
  // 💾 Sauvegarde équipe
  // =============================================================
  const handleSave = async () => {
    if (!equipe?.id) return;

    if (saving) return;

    try {
      setSaving(true);

      await api.put(`/equipes/${equipe.id}/agents`, {
        agentIds: selected,
      });

      toast.success("Équipe mise à jour !");
      onSuccess();
      onClose();

    } catch (err: any) {
      console.error("❌ Erreur mise à jour équipe :", err);

      if (err.response?.status === 401) {
        toast.error("🔐 Session expirée.");
        return;
      }

      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Erreur lors de la mise à jour.";

      toast.error(msg);

    } finally {
      setSaving(false);
    }
  };

return (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-6">
    <div className="bg-slate-800 w-full max-w-5xl h-[85vh] rounded-2xl shadow-2xl border border-slate-700 overflow-hidden text-gray-100 flex flex-col">

      {/* ================= HEADER ================= */}
      <div className="flex justify-between items-center bg-slate-900 px-8 py-5 border-b border-slate-700">
        <h2 className="text-xl font-semibold">
          ✏️ Modifier l’équipe :
          <span className="text-blue-400 ml-2">
            {equipe?.nom}
          </span>
        </h2>

        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition text-xl"
        >
          ✖
        </button>
      </div>

{/* ================= BODY ================= */}
<div className="flex-1 p-8 overflow-y-auto">

  {loading ? (
    <div className="text-center text-gray-400 py-10 animate-pulse">
      ⏳ Chargement des agents...
    </div>
  ) : agents.length === 0 ? (
    <p className="text-gray-500 italic text-center">
      Aucun agent disponible.
    </p>
  ) : (
    <>
      {/* ================= FILTRES ================= */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { key: "all", label: "Tous" },
          { key: "chef", label: "Chefs" },
          { key: "chauffeur", label: "Chauffeurs" },
          { key: "agent", label: "Agents" },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key as any)}
            className={`px-3 py-1 text-xs rounded-full transition ${
              filter === f.key
                ? "bg-blue-600 text-white"
                : "bg-slate-700 text-gray-300 hover:bg-slate-600"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* ================= LISTE AGENTS ================= */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredAgents.map((agent) => {
          const isChef =
            agent.fonction?.toLowerCase() === "chef";

          const isChauffeur =
            agent.fonction?.toLowerCase() === "chauffeur";

          const isInactive = agent.actif === false;

          return (
            <label
              key={agent.id}
              className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer border text-sm transition ${
                selected.includes(agent.id)
                  ? "bg-blue-700/40 border-blue-500"
                  : "bg-slate-700/50 border-slate-600 hover:bg-slate-600/70"
              } ${isInactive ? "opacity-50" : ""}`}
            >
              <input
                type="checkbox"
                checked={selected.includes(agent.id)}
                onChange={() => toggleAgent(agent.id)}
                disabled={isInactive}
                className="accent-blue-500 w-4 h-4"
              />

              <div className="flex flex-col">
                <span className="font-medium">
                  {agent.prenom
                    ? `${agent.prenom} ${agent.nom}`
                    : agent.nom}
                </span>

                {/* 👑 Chef */}
                {isChef && (
                  <span className="text-xs text-purple-400">
                    👑 Chef d’équipe
                  </span>
                )}

                {/* 🚛 Chauffeur */}
                {isChauffeur && (
                  <span className="text-xs text-emerald-400">
                    🚛 Chauffeur
                  </span>
                )}

                {/* 👤 Agent normal */}
                {!isChef && !isChauffeur && (
                  <span className="text-xs text-gray-400">
                    👤 Agent
                  </span>
                )}

                {/* 🔴 Inactif */}
                {isInactive && (
                  <span className="text-xs text-red-400">
                    🔴 Inactif
                  </span>
                )}
              </div>
            </label>
          );
        })}
      </div>
    </>
  )}
</div>

      {/* ================= FOOTER ================= */}
      <div className="flex justify-between items-center px-8 py-5 border-t border-slate-700 bg-slate-900">

        <span className="text-sm text-gray-400">
          {selected.length} agent(s) sélectionné(s)
        </span>

        <div className="flex gap-4">
          <button
            onClick={onClose}
            disabled={saving}
            className="bg-slate-700 hover:bg-slate-600 px-5 py-2 rounded-lg text-sm transition"
          >
            Annuler
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            className={`px-6 py-2 rounded-lg text-sm font-semibold text-white transition ${
              saving
                ? "bg-blue-900 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-500"
            }`}
          >
            {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  </div>
);

};
   
export default ModalEditEquipe;

